import React, { Fragment, useCallback, useEffect, useState } from 'react';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import Autoplay, { type AutoplayOptionsType } from 'embla-carousel-autoplay';
import { twMerge } from 'tailwind-merge';
import { ChevronIcon } from '@wbk/svg';
import { useRouter } from '@wbk/config';

import { Button } from '@wbk/ui';
import type { EmblaOptionsType } from 'embla-carousel';

type RenderPropHelpers = {
  selectedIndex: number;
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollTo: (index: number) => void;
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
};

type DotPropHelpers = Pick<RenderPropHelpers, 'selectedIndex'>;

type ThumbPropHelpers = Pick<RenderPropHelpers, 'scrollTo' | 'selectedIndex'>;

type Props = {
  /**
   * @deprecated no need to pass locale anymore
   */
  locale?: string;
  rootClassName?: string;
  containerClassName?: string;
  className?: string;
  thumbClassName?: string;
  options?: EmblaOptionsType;
  autoplay?: AutoplayOptionsType | boolean;
  renderHeader?: (props: RenderPropHelpers) => React.ReactNode;
  renderFooter?: (props: RenderPropHelpers) => React.ReactNode;
  onInit?: (emblaApi: UseEmblaCarouselType[1]) => void;
  onIndexChanged?: (index: number) => void;
  title?: React.ReactNode;
  arrows?: 'top' | 'middle';
  arrowsClassName?: string;
  dots?: boolean;
  renderDot?: (props: DotPropHelpers) => React.ReactNode;
  as?: React.ElementType;
  children: React.ReactNode[] | ((props: RenderPropHelpers) => React.ReactNode | React.ReactNode[]);
  thumbs?: (props: ThumbPropHelpers) => React.ReactNode | React.ReactNode[];
  dotsClassName?: string;
  enableOnDotsClick?: boolean;
};

export const Carousel = ({
  children,
  rootClassName,
  containerClassName,
  className,
  thumbClassName,
  options,
  autoplay,
  title = null,
  arrows,
  arrowsClassName,
  dots,
  renderDot,
  renderHeader,
  renderFooter,
  thumbs,
  onInit,
  onIndexChanged,
  as,
  dotsClassName,
  enableOnDotsClick = true,
}: Props) => {
  const Component = as || 'div';
  const { locale } = useRouter();
  const direction = locale?.startsWith('ar') ? 'rtl' : 'ltr';
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: 'start',
      direction,
      slidesToScroll: 'auto',
      ...options,
    },
    [
      Autoplay({
        active: !!autoplay,
        playOnInit: true,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
        ...(typeof autoplay === 'object' ? autoplay : {}),
      }),
    ]
  );

  const [emblaThumbsRef] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
    direction,
  });

  const onSelect = useCallback((emblaApi: UseEmblaCarouselType[1]) => {
    if (!emblaApi) return;
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());

    setSelectedIndex(emblaApi.selectedScrollSnap());
    if (onIndexChanged) {
      onIndexChanged(emblaApi.selectedScrollSnap());
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (emblaApi && onInit) {
      onInit(emblaApi);
    }
    // eslint-disable-next-line
  }, [emblaApi]);

  const scrollPrev = () => {
    if (emblaApi) emblaApi.scrollPrev();
  };

  const scrollNext = () => {
    if (emblaApi) emblaApi.scrollNext();
  };

  const scrollTo = (index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  };

  const helperProps = {
    selectedIndex,
    scrollPrev,
    scrollNext,
    scrollTo,
    prevBtnDisabled,
    nextBtnDisabled,
  };
  const elements = typeof children === 'function' ? children(helperProps) : children;
  const dotsList = Array.from({ length: emblaApi?.scrollSnapList()?.length || 0 }, (_, i) => i);

  return (
    <>
      {renderHeader && renderHeader(helperProps)}
      <div className={twMerge('relative', rootClassName)}>
        {(title || arrows === 'top') && (
          <div className={twMerge('pb-L gap-M flex justify-between', arrowsClassName)}>
            <div className='grow'>{title}</div>
            {(!prevBtnDisabled || !nextBtnDisabled) && arrows && arrows === 'top' && (
              <div className='hidden gap-2 md:flex'>
                <Button
                  shape='outlined'
                  theme='white'
                  className='rounded-S flex h-[30px] w-[30px] items-center justify-center border p-0 data-[hide=true]:hidden'
                  onClick={scrollPrev}
                  disabled={prevBtnDisabled}
                  data-hide={prevBtnDisabled && nextBtnDisabled}
                  aria-label='Previous'
                >
                  <ChevronIcon className='h-3 w-3 ltr:rotate-180' />
                </Button>

                <Button
                  shape='outlined'
                  theme='white'
                  className='rounded-S flex h-[30px] w-[30px] items-center justify-center border p-0 data-[hide=true]:hidden'
                  onClick={scrollNext}
                  disabled={nextBtnDisabled}
                  data-hide={prevBtnDisabled && nextBtnDisabled}
                  aria-label='Next'
                >
                  <ChevronIcon className='h-3 w-3 rtl:rotate-180' />
                </Button>
              </div>
            )}
          </div>
        )}

        <div className={twMerge('overflow-hidden', className)} ref={emblaRef}>
          <Component
            style={{ backfaceVisibility: 'hidden' }}
            className={twMerge('flex touch-pan-y', containerClassName)}
          >
            {elements}
          </Component>
        </div>

        {thumbs && (
          <div className={thumbClassName}>
            <div className='overflow-hidden' ref={emblaThumbsRef}>
              {thumbs({ selectedIndex, scrollTo })}
            </div>
          </div>
        )}

        {arrows === 'middle' && (
          <div
            className={twMerge(
              'absolute left-1/2 top-1/2 w-full -translate-x-1/2 ltr:[&>button:first-of-type]:left-0 rtl:[&>button:first-of-type]:right-0 ltr:[&>button:last-of-type]:right-0 rtl:[&>button:last-of-type]:left-0',
              arrowsClassName
            )}
          >
            <Button
              className='bg-body-level-1 absolute top-1/2 z-[1] m-2 flex h-11 w-11 -translate-y-1/2 cursor-pointer touch-manipulation appearance-none items-center justify-center p-0 aria-hidden:hidden'
              type='button'
              onClick={scrollPrev}
              disabled={prevBtnDisabled}
              aria-hidden={prevBtnDisabled}
              aria-label='Previous'
            >
              <svg
                width='30'
                height='30'
                viewBox='0 0 32 32'
                fill='none'
                className='fill-text rtl:rotate-180'
              >
                <path d='M20.9426 8.94263C21.4633 8.42194 21.4633 7.57772 20.9426 7.05703C20.4219 6.53633 19.5777 6.53633 19.057 7.05703L11.057 15.057C10.5523 15.5618 10.5346 16.3745 11.017 16.9007L18.3503 24.9007C18.8478 25.4435 19.6913 25.4802 20.2341 24.9826C20.7769 24.485 20.8136 23.6416 20.316 23.0988L13.8453 16.0399L20.9426 8.94263Z' />
              </svg>
            </Button>

            <Button
              className='bg-body-level-1 absolute top-1/2 z-[1] m-2 flex h-11 w-11 -translate-y-1/2 cursor-pointer touch-manipulation appearance-none items-center justify-center p-0 aria-hidden:hidden'
              type='button'
              onClick={scrollNext}
              disabled={nextBtnDisabled}
              aria-hidden={nextBtnDisabled}
              aria-label='Next'
            >
              <svg
                width='30'
                height='30'
                viewBox='0 0 32 32'
                fill='none'
                className='fill-text rotate-180 rtl:rotate-0'
              >
                <path d='M20.9426 8.94263C21.4633 8.42194 21.4633 7.57772 20.9426 7.05703C20.4219 6.53633 19.5777 6.53633 19.057 7.05703L11.057 15.057C10.5523 15.5618 10.5346 16.3745 11.017 16.9007L18.3503 24.9007C18.8478 25.4435 19.6913 25.4802 20.2341 24.9826C20.7769 24.485 20.8136 23.6416 20.316 23.0988L13.8453 16.0399L20.9426 8.94263Z' />
              </svg>
            </Button>
          </div>
        )}

        {dots && dotsList.length > 1 && (
          <div
            className={twMerge(
              'absolute bottom-5 left-0 right-0 z-[1] mx-auto flex w-fit translate-y-20 flex-wrap items-center justify-center rounded-full bg-transparent p-1.5',
              dotsClassName
            )}
          >
            {dotsList.map((index) => (
              <Fragment key={index}>
                {renderDot ? (
                  renderDot({ selectedIndex })
                ) : (
                  <button
                    type='button'
                    style={{
                      textDecoration: 'none',
                    }}
                    onClick={enableOnDotsClick ? () => scrollTo(index) : undefined}
                    className={twMerge(
                      'm-0 ml-1 mr-1 inline-flex h-[7px] w-[7px] touch-manipulation appearance-none items-center border-0 bg-transparent p-0 transition after:h-[7px] after:w-full after:rounded-full',
                      index === selectedIndex ? 'after:bg-text' : 'after:bg-text/40',
                      enableOnDotsClick ? 'cursor-pointer' : 'cursor-default'
                    )}
                  />
                )}
              </Fragment>
            ))}
          </div>
        )}
      </div>
      {renderFooter && renderFooter(helperProps)}
    </>
  );
};
